import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { iconX } from '@sorare/core/src/atoms/icons/system/iconX.ts';
import { FixtureStatus as CoreFixtureStatus } from '@sorare/core/src/components/lobby/FixtureStatus';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';

import { FixtureTimeline } from '#src/components/fixture/FixtureTimeline/index.tsx';
import { getFixtureState } from '#src/lib/so5.ts';

import type {
  FixtureStatus_composeTeamBenchObjectInterface,
  FixtureStatus_so5Leaderboard,
  FixtureStatus_so5Lineup,
} from './__generated__/index.graphql.ts';

const DropdownContent = styled.div`
  position: relative;
  isolation: isolate;
  padding: var(--double-unit);
`;
const StyledIconButton = styled(IconButton)`
  position: absolute;
  z-index: 1;
  right: var(--unit);
  top: var(--unit);
`;

type Props = {
  so5Leaderboard: FixtureStatus_so5Leaderboard;
  so5Lineup?: FixtureStatus_so5Lineup;
  benchObjects?: FixtureStatus_composeTeamBenchObjectInterface[];
  onClick?: () => void;
  detailsOpen?: boolean;
  setDetailsOpen?: (value: boolean) => void;
};

export const FixtureStatus = ({
  setDetailsOpen,
  detailsOpen,
  so5Lineup,
  benchObjects,
  onClick,
  so5Leaderboard,
}: Props) => {
  const { so5Fixture } = so5Leaderboard;
  const isLaptopAndAbove = useIsLaptop();
  const fixtureState = getFixtureState(so5Fixture);

  if (!fixtureState) {
    return null;
  }

  return (
    <Dropdown
      align="right"
      open={detailsOpen}
      onOpen={() => {
        setDetailsOpen?.(true);
      }}
      triggerOnHover
      gap={8}
      label={({ onClick: onDropdownClick, ...props }) => (
        <ButtonBase
          {...props}
          onClick={e => {
            onDropdownClick?.(e);
            setDetailsOpen?.(true);
            onClick?.();
          }}
        >
          <CoreFixtureStatus
            fixtureState={fixtureState}
            so5Leaderboard={so5Leaderboard}
            startDate={so5Leaderboard.cutOffDate}
            expectedRewardsDistributionDate={
              so5Fixture.expectedRewardsDistributionDate
            }
          />
        </ButtonBase>
      )}
      onClose={() => setDetailsOpen?.(false)}
    >
      {({ closeDropdown }) => (
        <DropdownContent>
          {!isLaptopAndAbove && (
            <StyledIconButton
              size="medium"
              color="transparent"
              icon={iconX}
              onClick={() => closeDropdown()}
            />
          )}
          <FixtureTimeline
            so5Leaderboard={so5Leaderboard}
            so5Lineup={so5Lineup}
            benchObjects={benchObjects}
          />
        </DropdownContent>
      )}
    </Dropdown>
  );
};

FixtureStatus.fragments = {
  so5Leaderboard: gql`
    fragment FixtureStatus_so5Leaderboard on So5Leaderboard {
      slug
      cutOffDate
      so5Fixture {
        slug
        startDate
        expectedRewardsDistributionDate
        ...getFixtureState_so5Fixture
      }
      ...LeaderboardStatus_so5Leaderboard
      ...FixtureTimeline_so5Leaderboard
    }
    ${getFixtureState.fragments.so5Fixture}
    ${FixtureTimeline.fragments.so5Leaderboard}
    ${CoreFixtureStatus.fragments.so5Leaderboard}
  ` as TypedDocumentNode<FixtureStatus_so5Leaderboard>,
  so5Lineup: gql`
    fragment FixtureStatus_so5Lineup on So5Lineup {
      id
      ...FixtureTimeline_so5Lineup
    }
    ${FixtureTimeline.fragments.so5Lineup}
  ` as TypedDocumentNode<FixtureStatus_so5Lineup>,
  composeTeamBenchObjectInterface: gql`
    fragment FixtureStatus_composeTeamBenchObjectInterface on ComposeTeamBenchObjectInterface {
      id
      ...FixtureTimeline_composeTeamBenchObjectInterface
    }
    ${FixtureTimeline.fragments.composeTeamBenchObjectInterface}
  ` as TypedDocumentNode<FixtureStatus_composeTeamBenchObjectInterface>,
};
